import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import {
  parseEmailType,
  parseGender,
  parsePhoneType,
} from '@/routes/contacts/-components/contactSchema';

import type { GetContactsData ,
  getContacts} from '../generated';
import { client } from '../generated/client.gen';
import {
  deleteContactsBulk,
  deleteContactsById,
  getContactsById,
  getContactsExport,
  postContacts,
  postContactsImport,
  postContactsImportEcomail,
  postContactsSearch,
  putContactsById,
} from '../generated';
import type { Contact, CreateContactRequest } from '../generated';
import type { ConditionTree } from './workspace';

export interface ListOptions {
  /** Server-side sort, as a Mongo field→direction map (1 asc, -1 desc). */
  sort?: Record<string, 1 | -1>;
  /** Projection: only these Mongo field paths are returned, slimming the payload. */
  fields?: string[];
}

const contactsQueryKeys = {
  list: (
    queryParams: GetContactsData['query'],
    conditions?: ConditionTree,
    query?: string,
    options?: ListOptions,
  ) => ['contacts', 'list', queryParams, conditions, query, options] as const,
  detail: (id: string) => ['contacts', 'detail', id] as const,
};

/**
 * Lists contacts. Always routes through POST /contacts/_search (the condition-tree
 * engine shared with segments) so server-side `sort` and the `fields` projection
 * apply uniformly — empty conditions behave exactly like the plain GET /contacts.
 */
function useList(
  queryParams: GetContactsData['query'],
  conditions?: ConditionTree,
  query?: string,
  options?: ListOptions,
) {
  const hasConditions = (conditions?.conditions?.length ?? 0) > 0;
  const trimmedQuery = query?.trim() ? query.trim() : undefined;
  return useSuspenseQuery({
    queryKey: contactsQueryKeys.list(
      queryParams,
      hasConditions ? conditions : undefined,
      trimmedQuery,
      options,
    ),
    queryFn: async () => {
      const { data } = await postContactsSearch({
        body: {
          conditions: hasConditions ? conditions : undefined,
          query: trimmedQuery,
          page: queryParams?.page ? queryParams.page + 1 : 1,
          pageSize: queryParams?.pageSize,
          sort: options?.sort,
          fields: options?.fields,
        },
      });
      if (!data) throw new Error('Failed to search contacts');
      return data as NonNullable<Awaited<ReturnType<typeof getContacts>>['data']>;
    },
  });
}

function useDetail(id: string) {
  return useSuspenseQuery({
    queryKey: contactsQueryKeys.detail(id),
    queryFn: async () => {
      const { data } = await getContactsById({ path: { id } });
      if (!data) throw new Error('Failed to fetch contact');
      return data;
    },
    select: (data) => ({
      ...data,
      profile: {
        ...data.profile,
        dateOfBirth: data.profile.dateOfBirth ? new Date(data.profile.dateOfBirth) : undefined,
        gender: parseGender(data.profile.gender),
      },
      contact: {
        ...data.contact,
        emails:
          data.contact.emails?.map((email) => ({
            ...email,
            type: parseEmailType(email.type),
          })) ?? [],
        phones:
          data.contact.phones?.map((phone) => ({
            ...phone,
            type: parsePhoneType(phone.type),
          })) ?? [],
      },
    }),
  });
}

function useCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateContactRequest) => {
      const { data } = await postContacts({ body });
      if (!data) throw new Error('Failed to create contact');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

function useDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, etag }: { id: string; etag: string }) =>
      deleteContactsById({
        path: { id },
        headers: { 'If-Match': etag },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

function useBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactIds: string[]) => deleteContactsBulk({ body: { contactIds } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

function useExport() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await getContactsExport({ responseType: 'blob' } as never);
      const blob = data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts_export.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export interface HashedExportOptions {
  algorithm: 'sha256' | 'hmac-sha256';
  hmacSecret?: string;
  usePrefixes: boolean;
  /** When true (default) the plain contact Id column is included; when false only Email/Phone are exported. */
  includeIds: boolean;
  selectedIds?: string[];
  query?: string;
  filters?: Record<string, unknown>;
  conditions?: ConditionTree;
}

function useExportHashed() {
  return useMutation({
    mutationFn: async (opts: HashedExportOptions) => {
      // Hand-written call: the hashed export endpoint isn't in the generated SDK.
      // The HMAC secret travels in the request body over HTTPS and is never stored.
      const res = await client.post({
        url: '/contacts/export/hashed',
        body: {
          algorithm: opts.algorithm,
          hmacSecret: opts.hmacSecret,
          usePrefixes: opts.usePrefixes,
          includeIds: opts.includeIds,
          selectedIds: opts.selectedIds,
          query: opts.query,
          filters: opts.filters,
          conditions: opts.conditions,
        },
        responseType: 'blob',
        throwOnError: true,
      });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts_hashed.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export interface PlainExportOptions {
  columns?: string[];
  selectedIds?: string[];
  query?: string;
  filters?: Record<string, unknown>;
  conditions?: ConditionTree;
}

function useExportPlain() {
  return useMutation({
    mutationFn: async (opts: PlainExportOptions) => {
      // Plain (decoded) export with a chosen column subset; not in the generated SDK.
      const res = await client.post({
        url: '/contacts/export/plain',
        body: {
          columns: opts.columns,
          selectedIds: opts.selectedIds,
          query: opts.query,
          filters: opts.filters,
          conditions: opts.conditions,
        },
        responseType: 'blob',
        throwOnError: true,
      });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export interface JsonExportOptions {
  selectedIds?: string[];
  query?: string;
  filters?: Record<string, unknown>;
  conditions?: ConditionTree;
}

function useExportJson() {
  return useMutation({
    mutationFn: async (opts: JsonExportOptions) => {
      // Full-data JSON export (complete contact records, no change log /
      // attachments / media); not in the generated SDK.
      const res = await client.post({
        url: '/contacts/export/json',
        body: {
          selectedIds: opts.selectedIds,
          query: opts.query,
          filters: opts.filters,
          conditions: opts.conditions,
        },
        responseType: 'blob',
        throwOnError: true,
      });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts.json';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

function useUploadAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      file,
      category,
    }: {
      id: string;
      file: File;
      category?: string;
    }) => {
      const form = new FormData();
      form.append('file', file);
      if (category) form.append('category', category);
      // Raw axios instance so the browser sets the multipart boundary and the
      // auth / X-Tenant-Id interceptors still apply.
      const res = await client.instance.post(`/contacts/${id}/attachments`, form);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(vars.id) });
    },
  });
}

function useDeleteAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fileId }: { id: string; fileId: string }) => {
      await client.instance.delete(`/contacts/${id}/attachments/${fileId}`);
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(vars.id) });
    },
  });
}

function useUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, contact, etag }: { id: string; contact: Contact; etag: string }) => {
      const { data } = await putContactsById({
        path: { id },
        body: contact,
        headers: { 'If-Match': etag },
      });
      if (!data) throw new Error('Failed to update contact');
      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

export interface JsonPatchOperation {
  op: 'add' | 'remove' | 'replace';
  path: string;
  value?: unknown;
}

function usePatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ops,
      etag,
    }: {
      id: string;
      ops: JsonPatchOperation[];
      etag: string;
    }) => {
      const res = await client.instance.patch<Contact>(`/contacts/${id}`, ops, {
        headers: { 'If-Match': etag, 'Content-Type': 'application/json-patch+json' },
      });
      return res.data;
    },
    onSuccess: (data, { id }) => {
      if (data) queryClient.setQueryData(contactsQueryKeys.detail(id), data);
      else queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
    onError: (_e, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(id) });
    },
  });
}

function useImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      source,
      format,
    }: {
      file: File;
      source: string;
      format: 'standard' | 'ecomail' | 'simple' | 'profile' | 'json';
    }) => {
      // The JSON endpoint reads the raw request body (not a multipart field), so send
      // the file's text with an application/json content type.
      if (format === 'json') {
        const res = await client.instance.post('/contacts/import/json', await file.text(), {
          params: { source },
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.data) throw new Error('Import failed');
        return res.data;
      }
      // The "simple" and "profile" endpoints aren't in the generated SDK; call them
      // directly with a multipart upload (the backend's UploadStream reads the `file`
      // field).
      if (format === 'simple' || format === 'profile') {
        const fd = new FormData();
        fd.append('file', file);
        const res = await client.instance.post(`/contacts/import/${format}`, fd, {
          params: { source },
        });
        if (!res.data) throw new Error('Import failed');
        return res.data;
      }
      // `source` is accepted by the backend as a query param but is not in the
      // generated schema, so we bypass the `query?: never` restriction here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const opts = { body: { file }, query: { source } } as any;
      const { data } =
        format === 'ecomail'
          ? await postContactsImportEcomail(opts)
          : await postContactsImport(opts);
      if (!data) throw new Error('Import failed');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

// ---- review / comments / delete-import (hand-written; not in the generated SDK) ----

function useSetReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      mode,
    }: {
      id: string;
      status: 'none' | 'requiresReview' | 'reviewed' | 'autoInvestigation' | 'investigated';
      mode?: 'manual' | 'automatic';
    }) => {
      await client.instance.post(`/contacts/${id}/review`, { status, mode });
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(vars.id) });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

function useBulkSetReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      status,
      mode,
    }: {
      ids: string[];
      status: 'none' | 'requiresReview' | 'reviewed' | 'autoInvestigation' | 'investigated';
      mode?: 'manual' | 'automatic';
    }) => {
      const res = await client.instance.post('/contacts/_bulk/review', { ids, status, mode });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

function useBulkSetActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, active }: { ids: string[]; active: boolean }) => {
      const res = await client.instance.post('/contacts/_bulk/status', { ids, active });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

// The field changes a "Bulk change" applies to many contacts at once. Only the
// members the user sets are sent; scalar fields / rating / status are replaced,
// addTags/addSources are unioned onto the existing arrays server-side (already
// assigned values stay untouched). Mirrors the api BulkEditFields DTO.
export interface BulkEditFields {
  interest?: string;
  occupation?: string;
  position?: string;
  specialization?: string;
  rating?: number;
  active?: boolean;
  doNotContact?: boolean;
  addTags?: string[];
  addSources?: string[];
}

function useBulkEditFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, fields }: { ids: string[]; fields: BulkEditFields }) => {
      const res = await client.instance.post('/contacts/_bulk/edit', { ids, fields });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

function useAddComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      await client.instance.post(`/contacts/${id}/comments`, { text });
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(vars.id) });
      // The contacts table renders a has-comments indicator from projected rows.
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

// Set or clear (blank text) the contact's single general note. Deliberately not
// etag-guarded — the backend does a field-scoped $set that can't clobber other
// concurrent edits, so the quick panel can save against a possibly stale row.
function useSetGeneralNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string | null }) => {
      await client.instance.put(`/contacts/${id}/general-note`, { text });
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: contactsQueryKeys.detail(vars.id) });
      // The contacts table renders a has-note indicator from projected rows.
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

export interface DeleteImportResult {
  total: number;
  matched: number;
  deleted: number;
  notFound: number;
  errors: { id?: string; error: string }[];
}

function useImportDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await client.instance.post('/contacts/import/delete', fd);
      return res.data as DeleteImportResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
    },
  });
}

export const contactsApi = {
  useList,
  useDetail,
  useCreate,
  useDelete,
  useBulkDelete,
  useExport,
  useExportHashed,
  useExportPlain,
  useExportJson,
  useUploadAttachment,
  useDeleteAttachment,
  useUpdate,
  usePatch,
  useImport,
  useSetReview,
  useBulkSetReview,
  useBulkSetActive,
  useBulkEditFields,
  useAddComment,
  useSetGeneralNote,
  useImportDelete,
};
